import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * The button for the dark surfaces: the call, the pre-join screen, and every
 * page outside a call.
 *
 * The light-theme `Button` cannot be reused here. Its palette is built for
 * white backgrounds, its `secondary` fill disappears against #101012, and its
 * focus ring is blue-on-white. Every dark surface was therefore hand-rolling
 * the same three buttons inline, which is how the pre-join footer and the
 * error panel ended up with different padding for the same control.
 *
 * Variants mirror the light Button so the two are interchangeable by name:
 * `primary` is the one action to take, `secondary` the way back, `danger`
 * for leaving or ending. Focus rings are white here rather than blue, because
 * blue on a dark blue-tinted surface is not visible enough to be a focus
 * indicator.
 */
const darkButtonVariants = cva(
  'inline-flex cursor-pointer select-none items-center justify-center gap-2 rounded-full text-[0.9375rem] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:pointer-events-none disabled:opacity-40 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-blue text-white hover:bg-blue-strong active:bg-blue',
        // A hairline rather than a fill: a grey button on a dark surface
        // reads as disabled, which is exactly wrong for the way out.
        secondary:
          'border border-white/20 text-call-ink hover:bg-white/10 active:bg-white/5',
        text: 'text-call-ink/80 hover:bg-white/10 hover:text-call-ink',
        danger: 'bg-hangup text-white hover:bg-[#ff5d52]',
      },
      size: {
        default: 'h-11 px-5 [&_svg]:size-4.5',
        sm: 'h-9 px-4 text-sm [&_svg]:size-4',
        icon: 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

interface DarkButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof darkButtonVariants> {
  asChild?: boolean;
}

const DarkButton = forwardRef<HTMLButtonElement, DarkButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(darkButtonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
DarkButton.displayName = 'DarkButton';

export { DarkButton, darkButtonVariants };
